import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { RouteScheduleSlotEditComponent } from './route-schedule-slot-edit/route-schedule-slot-edit.component';
import { RouteScheduleDuplicateComponent } from './route-schedule-duplicate/route-schedule-duplicate.component';
import { RouteScheduleResolveComponent } from './route-schedule-resolve/route-schedule-resolve.component';
import { RouteScheduleOverrideCreateComponent } from './route-schedule-override-create/route-schedule-override-create.component';
import { ConfirmDeleteComponent } from '../../users/edit-user/confirm-delete/confirm-delete.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { BackOffice24StatesService } from '../state/back-office24-states.service';

@Component({
  selector: 'app-route-schedules',
  templateUrl: './route-schedules.component.html',
  styleUrl: './route-schedules.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RouteSchedulesComponent implements OnInit, OnDestroy {
  templates: any[] = [];
  routeGroups: { routeId: string, routeName: string, templates: any[], overrides: any[] }[] = [];
  loading = false;

  readonly i18n = {
    noTemplates: { en: 'No route schedule templates found.', sv: 'Inga ruttschemamallar hittades.' },
    sickShuttlesTitle: { en: 'Shuttles Not In Schedule', sv: 'Skyttlar utanför schemat' },
    noSickShuttles: { en: 'No shuttles outside the schedule.', sv: 'Inga skyttlar utanför schemat.' },
    route: { en: 'Route', sv: 'Rutt' },
    date: { en: 'Date', sv: 'Datum' },
    time: { en: 'Time', sv: 'Tid' },
    count: { en: 'Count', sv: 'Antal' },
    resolve: { en: 'Resolve', sv: 'Lös' },
    specialSchedules: { en: 'Special schedules', sv: 'Specialscheman' },
    addSpecialSchedule: { en: 'Add special schedule', sv: 'Lägg till specialschema' },
    noSpecialSchedules: { en: 'No special schedules for this route.', sv: 'Inga specialscheman för denna rutt.' },
  };

  organization: MyConfigOrganization;
  sickShuttles: any[] = [];
  sickShuttleGroups: { routeName: string, time: string, shuttles: any[], expanded: boolean, selectedIds: Set<string> }[] = [];
  sickShuttlesLoading = false;
  private componentDestroyed$ = new Subject<boolean>();

  constructor(
    private dialog: MatDialog,
    private backofficeService: BackOfficeService,
    private snackBar: MatSnackBar,
    private backOffice24StatesService: BackOffice24StatesService,
  ) {
    this.loadTemplates();
  }

  ngOnInit(): void {
    this.backOffice24StatesService.currentOrganization$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(org => {
        this.organization = org;
        this.loadSickShuttles();
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  loadSickShuttles() {
    if (!this.organization) return;

    this.sickShuttlesLoading = true;
    this.backofficeService.getDashboardState(this.organization.organizationId).subscribe({
      next: (problems: any) => {
        this.sickShuttles = problems?.shuttlesNotInScheduleList || [];
        this.sickShuttleGroups = this.groupSickShuttles(this.sickShuttles);
        this.sickShuttlesLoading = false;
      },
      error: (error) => {
        console.error('Error loading shuttles not in schedule:', error);
        this.sickShuttles = [];
        this.sickShuttleGroups = [];
        this.sickShuttlesLoading = false;
      }
    });
  }

  private readonly swedishTimeFormatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  private readonly swedishDateFormatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  toSwedishTime(dateUtc: string): string {
    return this.swedishTimeFormatter.format(new Date(dateUtc));
  }

  toSwedishDate(dateUtc: string): string {
    return this.swedishDateFormatter.format(new Date(dateUtc));
  }

  private groupSickShuttles(shuttles: any[]): { routeName: string, time: string, shuttles: any[], expanded: boolean, selectedIds: Set<string> }[] {
    const groups = new Map<string, { routeName: string, time: string, shuttles: any[], expanded: boolean, selectedIds: Set<string> }>();

    shuttles.forEach(shuttle => {
      const time = this.toSwedishTime(shuttle.startDateUtc);
      const key = `${shuttle.routeName}|${time}`;

      if (!groups.has(key)) {
        groups.set(key, { routeName: shuttle.routeName, time, shuttles: [], expanded: false, selectedIds: new Set<string>() });
      }
      groups.get(key).shuttles.push(shuttle);
    });

    return Array.from(groups.values())
      .sort((a, b) => a.routeName.localeCompare(b.routeName) || a.time.localeCompare(b.time));
  }

  toggleSickShuttleGroup(group: { expanded: boolean }) {
    group.expanded = !group.expanded;
  }

  isShuttleSelected(group: { selectedIds: Set<string> }, shuttle: any): boolean {
    return group.selectedIds.has(shuttle.id);
  }

  toggleShuttleSelection(group: { selectedIds: Set<string> }, shuttle: any) {
    if (group.selectedIds.has(shuttle.id)) {
      group.selectedIds.delete(shuttle.id);
    } else {
      group.selectedIds.add(shuttle.id);
    }
  }

  isGroupFullySelected(group: { shuttles: any[], selectedIds: Set<string> }): boolean {
    return group.shuttles.length > 0 && group.shuttles.every(s => group.selectedIds.has(s.id));
  }

  isGroupPartiallySelected(group: { shuttles: any[], selectedIds: Set<string> }): boolean {
    return group.selectedIds.size > 0 && !this.isGroupFullySelected(group);
  }

  toggleSelectAllInGroup(group: { shuttles: any[], selectedIds: Set<string> }) {
    if (this.isGroupFullySelected(group)) {
      group.selectedIds.clear();
    } else {
      group.selectedIds = new Set(group.shuttles.map(s => s.id));
    }
  }

  resolveSickShuttleGroup(group: { routeName: string, time: string, shuttles: any[], selectedIds: Set<string> }) {
    const selectedShuttles = group.shuttles.filter(s => group.selectedIds.has(s.id));
    if (selectedShuttles.length === 0) return;

    const template = this.findCoveringTemplate(group.routeName, selectedShuttles);
    if (!template) {
      this.snackBar.open('The selected shuttles do not all fall within the same schedule.', 'Close', { duration: 4000 });
      return;
    }

    const slotOptions = this.buildSlotOptions(template, group.time);
    if (slotOptions.length === 0) {
      this.snackBar.open('This schedule has no other timeslots to reschedule to.', 'Close', { duration: 4000 });
      return;
    }

    const dialogRef = this.dialog.open(RouteScheduleResolveComponent, {
      width: '450px',
      data: {
        routeName: group.routeName,
        time: group.time,
        shuttleIds: selectedShuttles.map(s => s.id),
        shuttleCount: selectedShuttles.length,
        slotOptions
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        const message = result.failedCount > 0
          ? `Resolved ${result.resolvedCount}, failed ${result.failedCount} (capacity did not fit).`
          : `Resolved ${result.resolvedCount} shuttle${result.resolvedCount === 1 ? '' : 's'}.`;
        this.snackBar.open(message, 'Close', { duration: 4000 });
        this.loadSickShuttles();
      }
    });
  }

  // Finds the single route-schedule template that applies to every selected shuttle's date - returns
  // null if the shuttles span more than one template (or none). Mirrors the backend's resolution order
  // (GetSlotsForPeriod): a Manual/DayOfYear/DayOfWeek override for the date wins over the regular,
  // range-based Always template.
  private findCoveringTemplate(routeName: string, shuttles: any[]): any {
    const routeGroup = this.routeGroups.find(g => g.routeName === routeName);
    if (!routeGroup) return null;

    const shuttleDates = shuttles.map(s => this.toSwedishDate(s.startDateUtc));
    const resolved = shuttleDates.map(date => this.resolveTemplateForDate(routeGroup, date));

    return resolved.every(t => t && t === resolved[0]) ? resolved[0] : null;
  }

  private resolveTemplateForDate(routeGroup: { templates: any[], overrides: any[] }, swedishDate: string): any {
    const manualMatch = routeGroup.overrides.find(o => o.assignmentType === 'Manual' && o.assignmentValue === swedishDate);
    if (manualMatch) return manualMatch;

    const dayOfYear = swedishDate.substring(5); // 'YYYY-MM-DD' -> 'MM-DD'
    const dayOfYearMatch = routeGroup.overrides.find(o => o.assignmentType === 'DayOfYear' && this.toDayOfYearKey(o.assignmentValue) === dayOfYear);
    if (dayOfYearMatch) return dayOfYearMatch;

    const weekday = new Date(swedishDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const dayOfWeekMatch = routeGroup.overrides.find(o => o.assignmentType === 'DayOfWeek' && o.assignmentValue === weekday);
    if (dayOfWeekMatch) return dayOfWeekMatch;

    return routeGroup.templates.find((template: any) => this.isDateWithinPeriod(swedishDate, template)) || null;
  }

  // Backend stores DayOfYear as 'dd.MM'; normalize to 'MM-DD' for comparison against a 'YYYY-MM-DD' date.
  private toDayOfYearKey(ddMM: string): string {
    const [dd, mm] = (ddMM || '').split('.');
    return dd && mm ? `${mm}-${dd}` : '';
  }

  private isDateWithinPeriod(swedishDate: string, template: any): boolean {
    // swedishDate is 'YYYY-MM-DD' (sv-SE Intl format), assignmentDateFrom/To are ISO date strings too.
    const from = (template.assignmentDateFrom || '').substring(0, 10);
    const to = (template.assignmentDateTo || '').substring(0, 10);
    if (!from || !to) return false;
    return swedishDate >= from && swedishDate <= to;
  }

  // Builds the "3 before / 3 after" timeslot list from the template's own slots, ordered by time of
  // day, wrapping around the end/start of the list when there aren't enough neighbors on one side.
  private buildSlotOptions(template: any, currentTime: string): string[] {
    const times = (template.slots || [])
      .map((slot: any) => this.formatTimeForInput(slot.Time))
      .sort((a: string, b: string) => a.localeCompare(b));

    if (times.length === 0) return [];

    let index = times.indexOf(currentTime);
    if (index === -1) {
      // Current time isn't an exact slot match; place it where it would sort, so neighbors are still sensible.
      index = times.findIndex((t: string) => t > currentTime);
      if (index === -1) index = times.length;
    }

    const before: string[] = [];
    for (let i = 1; i <= 3; i++) {
      before.unshift(times[(index - i + times.length * 2) % times.length]);
    }

    const after: string[] = [];
    for (let i = 1; i <= 3; i++) {
      after.push(times[(index + (i - 1)) % times.length]);
    }

    return [...new Set([...before, ...after])].filter(t => t !== currentTime);
  }

  private formatTimeForInput(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
  }

  private earliestDateFrom(template: any): number {
    return template.assignmentDateFrom ? new Date(template.assignmentDateFrom).getTime() : Infinity;
  }

  isFirstInGroup(template: any, group: { routeName: string, templates: any[] }): boolean {
    if (!group.templates.length) return false;
    const first = group.templates.reduce((earliest, t) =>
      this.earliestDateFrom(t) < this.earliestDateFrom(earliest) ? t : earliest
    );
    return first === template;
  }

  isPast(template: any): boolean {
    if (!template.assignmentDateTo) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(template.assignmentDateTo) < today;
  }

  canDelete(template: any, group: { routeName: string, templates: any[] }): boolean {
    if (group.templates.length <= 1) return false;
    return this.isPast(template) || !this.isFirstInGroup(template, group);
  }

  deleteTemplate(template: any) {
    const message = this.isOverride(template)
      ? `Delete "${template.name}"? This special schedule will no longer apply.`
      : this.isPast(template)
        ? `Delete "${template.name}"? Its period has already ended.`
        : `Delete "${template.name}"? Its period will be merged into the preceding template.`;

    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message,
        buttonText: { ok: 'Delete', cancel: 'Cancel' }
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.backofficeService.deleteRouteScheduleTemplate(template.id).subscribe({
        next: () => {
          this.loadTemplates();
          this.loadSickShuttles();
        },
        error: (error) => {
          console.error('Error deleting route schedule template:', error);
          const message = (typeof error?.error === 'string' && error.error) || 'Could not delete the template';
          this.snackBar.open(message, 'Close', { duration: 3000 });
        }
      });
    });
  }

  loadTemplates() {
    this.loading = true;
    this.backofficeService.getRouteScheduleTemplates().subscribe({
      next: (templates) => {
        this.templates = templates || [];
        this.routeGroups = this.groupByRoute(this.templates);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading route schedule templates:', error);
        this.templates = [];
        this.routeGroups = [];
        this.loading = false;
      }
    });
  }

  private isOverride(template: any): boolean {
    return template.assignmentType !== 'Always';
  }

  private groupByRoute(templates: any[]): { routeId: string, routeName: string, templates: any[], overrides: any[] }[] {
    const groups = new Map<string, any[]>();
    templates.forEach(template => {
      const key = template.routeName || 'No route';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(template);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([routeName, groupTemplates]) => ({
        routeId: groupTemplates.find(t => t.entityId)?.entityId || null,
        routeName,
        templates: groupTemplates.filter(t => !this.isOverride(t)).sort((a, b) => this.earliestDateFrom(a) - this.earliestDateFrom(b)),
        overrides: groupTemplates.filter(t => this.isOverride(t))
      }));
  }

  overrideDescription(template: any): string {
    const value = template.assignmentValue;
    const base = (() => {
      switch (template.assignmentType) {
        case 'DayOfWeek':
          return `Every ${value}`;
        case 'DayOfYear':
          return `Every year on ${value}`;
        case 'Manual':
          return `On ${value}`;
        default:
          return value || '';
      }
    })();

    const from = template.assignmentDateFrom;
    const to = template.assignmentDateTo;
    if (!from && !to) return base;
    if (from && to) return `${base} (${from} – ${to})`;
    if (from) return `${base} (from ${from})`;
    return `${base} (until ${to})`;
  }

  addOverride(group: { routeId: string, routeName: string, templates: any[], overrides: any[] }) {
    if (!group.routeId) return;

    const dialogRef = this.dialog.open(RouteScheduleOverrideCreateComponent, {
      width: '800px',
      data: { routeId: group.routeId, routeTemplates: group.templates, routeOverrides: group.overrides }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        this.loadSickShuttles();
      }
    });
  }

  editOverride(template: any, group: { routeId: string, templates: any[], overrides: any[] }) {
    const dialogRef = this.dialog.open(RouteScheduleOverrideCreateComponent, {
      width: '800px',
      data: {
        routeId: group.routeId,
        template,
        routeTemplates: group.templates,
        routeOverrides: group.overrides.filter(o => o.id !== template.id)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        this.loadSickShuttles();
      }
    });
  }

  editTemplate(template: any) {
    const dialogRef = this.dialog.open(RouteScheduleSlotEditComponent, {
      width: '800px',
      data: { template }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        this.loadSickShuttles();
      }
    });
  }

  duplicateTemplate(template: any) {
    const dialogRef = this.dialog.open(RouteScheduleDuplicateComponent, {
      width: '450px',
      data: { template }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadTemplates();
        this.loadSickShuttles();
      }
    });
  }
}
